import { booleanLookup } from '../../../common/constants/common';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody, ISearchResponse } from '../../../common/models/common';
import { ITabVLicense } from '../../../services/rvTools/tabVLicense/tabVLicense.model';
import { RootState } from '../../app.model';
import {
  searchTabVLicense,
  getTabVLicenseById,
  saveTabVLicense,
  deleteTabVLicense,
} from './tabVLicense.action';
import { ITabVLicenseState } from './tabVLicense.model';

export const initialState: ITabVLicenseState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const tabVLicenseSlice = createSlice({
  name: 'tabVLicense',
  initialState,
  reducers: {
    clearTabVLicense: () => {
      return initialState;
    },
    clearTabVLicenseMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
    },
    clearTabVLicenseGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchTabVLicense.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchTabVLicense.fulfilled.type]: (
      state,
      action: PayloadAction<ISearchResponse<ITabVLicense>>
    ) => {
      const { search_result, ...rest } = action.payload;
      state.search.data = search_result.records;
      state.search.count = search_result.total_count;
      if (JSON.stringify(rest) !== '{}') {
        state.search.lookups = { ...rest, booleanLookup };
      }
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = search_result.table_name;
      if (search_result.column_selection) {
        state.tableColumnSelection.id = search_result.column_selection.id;
        const tableSelectionObj = JSON.parse(search_result.column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = search_result.table_name;
    },
    [searchTabVLicense.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getTabVLicenseById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getTabVLicenseById.fulfilled.type]: (state, action: PayloadAction<ITabVLicense>) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getTabVLicenseById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveTabVLicense.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveTabVLicense.fulfilled.type]: (state, action: PayloadAction<IApiResponseBody<unknown>>) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveTabVLicense.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteTabVLicense.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteTabVLicense.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteTabVLicense.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

// A selector
export const tabVLicenseSelector = (state: RootState) => state.tabVLicense;

// Actions
export const {
  clearTabVLicense,
  clearTabVLicenseMessages,
  clearTabVLicenseGetById,
  setTableColumnSelection,
} = tabVLicenseSlice.actions;

// The reducer
export default tabVLicenseSlice.reducer;
